// API Integrations through ClinicOS API (i.e. Pipedrive)

import { config } from "../config";
import { TSession } from "../utils/types";
import { IntegrationType } from "../utils/integrationTypes";
import type {
  TCreateIntegrationRequest,
  TIntegration,
} from "../utils/integrationTypes";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";
import { logUIError } from "./api-misc";
export type {
  TCreateIntegrationRequest,
  TIntegration,
  TPipedriveApiKeyCredentials,
  TPipedriveOAuthCredentials,
  TPipedriveLeadConfig,
  TMetaPixelConfig,
  TMetaPixelCredentials,
  TWebhookIntegrationConfig,
  TWebhookFieldMapping,
} from "../utils/integrationTypes";

const protocol = config.coreApi.protocol;
const host = config.coreApi.host;

export type TExchangePipedriveTokenRequest = {
  code: string;
};

export type TExchangePipedriveTokenResponse = {
  message: string;
  access_token: string;
};

export const exchangePipedriveToken = async (
  code: string,
): Promise<TExchangePipedriveTokenResponse> => {
  try {
    const response = await fetch(
      `${protocol}://${host}/integrations/v1/pipedrive/exchange-token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code }),
      },
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Pipedrive exchange failed: ${error}`);
    }

    const data: TExchangePipedriveTokenResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error exchanging Pipedrive token:", error);
    logUIError({
      message: "Pipedrive token exchange failed",
      page_url:
        typeof window !== "undefined" ? window.location.href : undefined,
      context: {
        component: "api-integrations",
        integration: "pipedrive",
        error: error instanceof Error ? error.message : String(error),
      },
    }).catch((logErr) =>
      console.warn("logUIError failed in api-integrations", logErr),
    );
    throw error;
  }
};

const baseUrl = `${config.coreApi.protocol}://${config.coreApi.host}/clinics/v1/integration`;

export const createIntegration = async (
  session: TSession,
  payload: TCreateIntegrationRequest,
): Promise<TIntegration> => {
  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/integrations`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) throw new Error("Failed to create integration");
  return await response.json();
};

export const listIntegrations = async (
  session: TSession,
): Promise<TIntegration[]> => {
  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/integrations`,
    {
      method: "GET",
    },
  );
  if (!response.ok) throw new Error("Failed to fetch integrations");
  return await response.json();
};

export const getIntegration = async (
  session: TSession,
  integrationId: number,
): Promise<TIntegration> => {
  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/integrations/${integrationId}`,
    {
      method: "GET",
    },
  );
  if (!response.ok) throw new Error("Failed to fetch integration");
  return await response.json();
};

export const updateIntegration = async (
  session: TSession,
  integrationId: number,
  payload: TCreateIntegrationRequest,
): Promise<TIntegration> => {
  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/integrations/${integrationId}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) throw new Error("Failed to update integration");
  return await response.json();
};

export const deleteIntegration = async (
  session: TSession,
  integrationId: number,
): Promise<void> => {
  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/integrations/${integrationId}`,
    {
      method: "DELETE",
    },
  );
  if (!response.ok) throw new Error("Failed to delete integration");
};

// Auto sync notes
export type TAutoSyncWithIntegrationRequest = {
  note_id: number;
  appointment_id: number;
};

// Add this function at the end of your file
export const autoSyncWithIntegration = async (
  session: TSession,
  payload: TAutoSyncWithIntegrationRequest,
): Promise<void> => {
  const response = await fetchWithAuthRetry(
    `${protocol}://${host}/clinics/v1/documentation/${session.clinicId}/auto-sync-with-integration`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) throw new Error("Failed to auto-sync with integration");
};

export type TAutoSyncNotesResponse = {
  integration_id: number;
  is_auto_sync_notes: boolean;
};

export const setAutoSyncNotes = async (
  session: TSession,
  integrationId: number,
  autoSyncNotes: boolean,
): Promise<TAutoSyncNotesResponse> => {
  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/integrations/${integrationId}/set-auto-sync-notes?auto_sync_notes=${autoSyncNotes}`,
    {
      method: "PATCH",
    },
  );
  if (!response.ok) throw new Error("Failed to update auto sync setting");
  return await response.json();
};

export type TIntegrationTypeMetadata = {
  integration_type: string;
  display_name: string;
  description: string;
  supports_auto_sync_notes: boolean;
  supports_is_active: boolean;
  enabled: boolean;
  disabled_reason?: string | null;
};

export type TIntegrationSchemaExample = {
  name: string;
  payload: Record<string, unknown>;
};

export type TIntegrationSchemaDiscoveryResponse = {
  integration_type: string;
  schema_version: string;
  create_schema: Record<string, unknown>;
  update_schema: Record<string, unknown>;
  ui_schema: Record<string, Record<string, string>>;
  template: Record<string, unknown>;
  examples: TIntegrationSchemaExample[];
  enabled: boolean;
  disabled_reason?: string | null;
};

export type TIntegrationSchemasAllResponse = {
  schema_version: string;
  schemas: TIntegrationSchemaDiscoveryResponse[];
};

export type TIntegrationCreatePayloadV2 = {
  integration_type: string;
  credentials?: Record<string, unknown> | null;
  config?: Record<string, unknown> | null;
  is_active?: boolean;
  auto_sync_notes?: boolean | null;
};

export type TIntegrationTestPayloadV2 = {
  integration_type: string;
  credentials: Record<string, unknown>;
  config?: Record<string, unknown> | null;
};

export type TIntegrationTestResponseV2 = {
  integration_type: string;
  valid: boolean;
  message: string;
  pixel_id?: string | null;
  error_code?: "invalid_credentials" | "resource_access_denied" | null;
};

export type TBackendValidationErrorDetail = {
  loc?: Array<string | number>;
  msg?: string;
  type?: string;
};

export class IntegrationCreateError extends Error {
  status: number;
  details?: TBackendValidationErrorDetail[];

  constructor(
    message: string,
    status: number,
    details?: TBackendValidationErrorDetail[],
  ) {
    super(message);
    this.name = "IntegrationCreateError";
    this.status = status;
    this.details = details;
  }
}

export const listIntegrationTypes = async (
  session: TSession,
): Promise<TIntegrationTypeMetadata[]> => {
  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/integration-types`,
    { method: "GET" },
  );
  if (!response.ok) throw new Error("Failed to fetch integration types");
  return await response.json();
};

export const getIntegrationSchema = async (
  session: TSession,
  integrationType: string,
): Promise<TIntegrationSchemaDiscoveryResponse> => {
  const encodedType = encodeURIComponent(integrationType);
  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/integrations/schema?integration_type=${encodedType}`,
    {
      method: "GET",
    },
  );
  if (!response.ok)
    throw new Error(`Failed to fetch schema for ${integrationType}`);
  return await response.json();
};

export const getAllIntegrationSchemas = async (
  session: TSession,
): Promise<TIntegrationSchemasAllResponse> => {
  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/integrations/schema/all`,
    {
      method: "GET",
    },
  );
  if (!response.ok) throw new Error("Failed to fetch integration schemas");
  return await response.json();
};

export const createIntegrationV2 = async (
  session: TSession,
  payload: TIntegrationCreatePayloadV2,
): Promise<TIntegration> => {
  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/integrations`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const rawBody = await response.text();
    let detailMessage = "Failed to create integration";
    let details: TBackendValidationErrorDetail[] | undefined;
    try {
      const parsed = JSON.parse(rawBody);
      if (Array.isArray(parsed?.detail)) {
        details = parsed.detail;
        detailMessage = parsed.detail
          .map((entry: TBackendValidationErrorDetail) => entry.msg)
          .filter(Boolean)
          .join("; ");
      } else if (typeof parsed?.detail === "string") {
        detailMessage = parsed.detail;
      } else if (typeof parsed?.message === "string") {
        detailMessage = parsed.message;
      }
    } catch {
      if (rawBody) {
        detailMessage = rawBody;
      }
    }
    throw new IntegrationCreateError(detailMessage, response.status, details);
  }

  return await response.json();
};

export const testIntegrationV2 = async (
  session: TSession,
  payload: TIntegrationTestPayloadV2,
): Promise<TIntegrationTestResponseV2> => {
  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/integrations/test`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to verify integration");
  }

  return await response.json();
};
